import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Lock, Mail, Phone, CheckCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PIE = ['#7851A9', '#9B72CF', '#C4A7E7', '#E9C46A', '#2A9D8F', '#E76F51'];
const fmtUSD = (v) => (v < 0 ? '-$' : '$') + Math.abs(v).toLocaleString();
const fmtM = (v) => `$${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}M`;
const fmtK = (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v);
const pill = { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700 };
const chartCard = { background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' };
const chartH = { margin: '0 0 10px', fontSize: '13px', color: '#7851A9', fontWeight: 700 };

const InvestorPortal = () => {
  const [step, setStep] = useState(1); // 1=register, 2=nda, 3=verify, 4=portal
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [investorId, setInvestorId] = useState('');
  const [ndaText, setNdaText] = useState('');
  const [code, setCode] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [portalData, setPortalData] = useState(null);
  const [investorEmail, setInvestorEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ndaScrolled, setNdaScrolled] = useState(false);

  // Content protection
  useEffect(() => {
    const preventCopy = (e) => { if (step === 4) e.preventDefault(); };
    const preventContext = (e) => { if (step === 4) e.preventDefault(); };
    const preventPrint = (e) => { if (step === 4 && (e.ctrlKey || e.metaKey) && e.key === 'p') e.preventDefault(); };
    const preventScreenshot = (e) => {
      if (step === 4 && e.key === 'PrintScreen') {
        e.preventDefault();
        navigator.clipboard.writeText('');
      }
    };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', preventPrint);
    document.addEventListener('keydown', preventScreenshot);

    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', preventPrint);
      document.removeEventListener('keydown', preventScreenshot);
    };
  }, [step]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/marketing/investor/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      setInvestorId(data.investor_id);
      // Fetch NDA
      const ndaRes = await fetch(`${API}/api/marketing/investor/nda`);
      const ndaData = await ndaRes.json();
      setNdaText(ndaData.nda_text);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptNDA = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/marketing/investor/accept-nda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ investor_id: investorId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      if (data.fallback_code) {
        setCode(data.fallback_code);
      }
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/marketing/investor/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ investor_id: investorId, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      setAccessToken(data.access_token);
      setInvestorEmail(data.email);
      // Fetch portal data
      const portalRes = await fetch(`${API}/api/marketing/investor/portal`, {
        headers: { 'Authorization': `Bearer ${data.access_token}` },
      });
      const portalContent = await portalRes.json();
      if (!portalRes.ok) throw new Error(portalContent.detail || 'Access denied');
      setPortalData(portalContent);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNdaScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setNdaScrolled(true);
    }
  }, []);

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch(`${API}/api/marketing/investor/portal-pdf`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SpeeVDee-Business-Plan.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not download the PDF. Please try again.');
    }
  };

  // Step 4: Protected portal content
  if (step === 4 && portalData) {
    const c = portalData.content;
    return (
      <div className="investor-portal-page" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
        <style>{`
          @media print { .investor-portal-page { display: none !important; } body::after { content: "Printing is disabled for confidential content."; display: block; font-size: 24px; text-align: center; padding: 100px; } }
          .investor-portal-page * { user-select: none !important; -webkit-user-select: none !important; }
          .investor-portal-page img { pointer-events: none; -webkit-user-drag: none; }
        `}</style>

        {/* Watermark overlay */}
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: `${(i % 4) * 28 - 5}%`,
              top: `${Math.floor(i / 4) * 35 + 5}%`,
              transform: 'rotate(-35deg)',
              fontSize: '14px',
              color: 'rgba(120, 81, 169, 0.06)',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              letterSpacing: '2px',
            }}>
              CONFIDENTIAL — {investorEmail}
            </div>
          ))}
        </div>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1F2937, #111827)', color: '#fff', padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#7851A9', fontWeight: 700, fontSize: '13px', letterSpacing: '2px', marginBottom: '8px' }}>NIGHTEENGALE GROUP LTD.</p>
          <h1 style={{ fontSize: '32px', fontWeight: 800, margin: 0 }}>Spee'V'Dee Investor Portal</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '8px' }}>Confidential — NDA Protected — Viewing as {investorEmail}</p>
          <button data-testid="download-pdf-btn" onClick={handleDownloadPdf} style={{ marginTop: '16px', background: 'linear-gradient(135deg,#E9C46A,#d4a843)', color: '#1F2937', border: 'none', padding: '11px 22px', borderRadius: '999px', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(233,196,106,0.4)' }}>
            <ArrowRight size={16} style={{ transform: 'rotate(90deg)' }} /> Download Business Plan (PDF)
          </button>
        </div>

        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 20px' }}>
          {/* ===== BUSINESS PLAN COVER ===== */}
          {c.investment && (
            <div data-testid="bp-cover" style={{ position: 'relative', borderRadius: '18px', overflow: 'hidden', marginBottom: '32px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
              <img src="/investor/cover.webp" alt="Nighteengale" style={{ width: '100%', height: '270px', objectFit: 'cover', display: 'block' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,23,42,0.5), rgba(15,23,42,0.88))' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px' }}>
                <p style={{ color: '#E9C46A', fontWeight: 800, letterSpacing: '3px', fontSize: '13px', marginBottom: '10px' }}>NIGHTEENGALE GROUP LTD.</p>
                <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: 900, margin: 0, letterSpacing: '0.5px' }}>Business Plan & Investment Memorandum</h1>
                <p style={{ color: '#E9C46A', fontWeight: 700, marginTop: '6px', letterSpacing: '1px' }}>Spee'V'Dee — a Nighteengale Group company</p>
                <div style={{ marginTop: '14px', display: 'inline-flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <span style={pill}>Raising {c.investment.raising}</span>
                  <span style={pill}>{c.investment.equity_offered} Equity</span>
                  <span style={pill}>{c.investment.post_money_valuation} Post-Money</span>
                </div>
              </div>
            </div>
          )}

          {/* ===== THE ASK ===== */}
          {c.investment && (
            <>
              <SectionTitle>The Investment Opportunity</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '14px', marginBottom: '18px' }}>
                <StatCard label="Raising" value={c.investment.raising} accent />
                <StatCard label="Equity Offered" value={c.investment.equity_offered} />
                <StatCard label="Pre-Money" value={c.investment.pre_money_valuation} />
                <StatCard label="Post-Money" value={c.investment.post_money_valuation} />
              </div>
              <div style={{ background: '#fff', borderRadius: '12px', padding: '18px 20px', marginBottom: '32px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', fontSize: '13.5px', color: '#374151', lineHeight: 1.6 }}>
                <p style={{ margin: '0 0 6px' }}><strong style={{ color: '#7851A9' }}>Instrument:</strong> {c.investment.instrument}</p>
                <p style={{ margin: '0 0 6px' }}><strong style={{ color: '#7851A9' }}>Use of Capital:</strong> {c.investment.use_of_capital}</p>
                <p style={{ margin: 0 }}><strong style={{ color: '#7851A9' }}>Runway:</strong> {c.investment.runway}</p>
              </div>
            </>
          )}

          {/* ===== USE OF FUNDS ===== */}
          {c.use_of_funds && c.charts && (
            <>
              <SectionTitle>Use of Funds — Advertising & Activation</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '20px', marginBottom: '32px', alignItems: 'center' }}>
                <div style={chartCard}>
                  <ResponsiveContainer width="100%" height={260}>
                    <RePieChart>
                      <Pie data={c.charts.use_of_funds} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.value}%`}>
                        {c.charts.use_of_funds.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${v}%`} />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  {c.use_of_funds.map((u, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: PIE[i % PIE.length], flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '13px', color: '#374151' }}>{u.category}</span>
                      <span style={{ fontWeight: 700, fontSize: '13px' }}>{u.amount}</span>
                      <span style={{ fontSize: '12px', color: '#9CA3AF', width: '34px', textAlign: 'right' }}>{u.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ===== PILOT MARKETS ===== */}
          {c.pilot_markets && c.charts && (
            <>
              <SectionTitle>5-Country Pilot Strategy</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '14px', marginBottom: '16px' }}>
                {c.pilot_markets.map((m, i) => (
                  <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderTop: `3px solid ${PIE[i % PIE.length]}` }}>
                    <div style={{ fontSize: '26px' }}>{m.flag}</div>
                    <h4 style={{ margin: '6px 0 2px', fontSize: '15px', color: '#1F2937' }}>{m.country}</h4>
                    <p style={{ margin: 0, fontSize: '11px', color: '#7851A9', fontWeight: 700 }}>{m.tier} • {m.hub}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0', fontSize: '12px' }}>
                      <span style={{ color: '#6B7280' }}>Activation</span><strong>{m.budget}</strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '11.5px', color: '#6B7280', lineHeight: 1.5 }}>{m.rationale}</p>
                  </div>
                ))}
              </div>
              <div style={{ ...chartCard, marginBottom: '32px' }}>
                <h4 style={chartH}>Activation Budget Allocation (% of $3M)</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={c.charts.pilot_activation}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="country" fontSize={12} />
                    <YAxis fontSize={12} unit="%" />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="pct" radius={[6, 6, 0, 0]}>
                      {c.charts.pilot_activation.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* ===== KEY ASSUMPTIONS ===== */}
          {c.key_assumptions && (
            <>
              <SectionTitle>Model Assumptions</SectionTitle>
              <div style={{ background: '#fff', borderRadius: '12px', padding: '18px 22px', marginBottom: '28px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: '4px solid #7851A9' }}>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  {c.key_assumptions.map((a, i) => (<li key={i} style={{ fontSize: '12.5px', color: '#374151', lineHeight: 1.7, marginBottom: '6px' }}>{a}</li>))}
                </ul>
              </div>
            </>
          )}

          {/* ===== 3-YEAR CASH FLOW CHARTS ===== */}
          {c.charts && (
            <>
              <SectionTitle>3-Year Cash-Flow Projection</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '20px', marginBottom: '20px' }}>
                <div style={chartCard}>
                  <h4 style={chartH}>Revenue vs. Expenses vs. Net Income</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={c.charts.cash_flow}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis fontSize={11} tickFormatter={fmtM} />
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#7851A9" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" name="Expenses" fill="#E76F51" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="net" name="Net Income" stroke="#059669" strokeWidth={3} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div style={chartCard}>
                  <h4 style={chartH}>Ending Cash Balance (starts with $3M raise)</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={c.charts.cash_flow}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis fontSize={11} tickFormatter={fmtM} />
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                      <Line type="monotone" dataKey="ending_cash" name="Cash" stroke="#7851A9" strokeWidth={3} dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={chartCard}>
                  <h4 style={chartH}>User & Delivery Growth</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={c.charts.growth}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis fontSize={11} tickFormatter={fmtK} />
                      <Tooltip formatter={(v) => v.toLocaleString()} />
                      <Legend />
                      <Bar dataKey="users" name="Active Users" fill="#9B72CF" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="deliveries" name="Deliveries" fill="#E9C46A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={chartCard}>
                  <h4 style={chartH}>Year-3 Revenue Mix</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <RePieChart>
                      <Pie data={c.charts.revenue_mix_year3} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => fmtM(e.value)}>
                        {c.charts.revenue_mix_year3.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* Value Propositions */}
          <SectionTitle>Value Propositions</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <PortalCard title="For Senders" accent>{c.value_propositions.senders}</PortalCard>
            <PortalCard title="For Flight Attendants">{c.value_propositions.flight_attendants}</PortalCard>
            <PortalCard title="For Travelers">{c.value_propositions.travelers}</PortalCard>
          </div>

          {/* Revenue Streams */}
          <SectionTitle>Revenue Streams</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <PortalCard title="Subscriptions">{c.revenue_streams.subscriptions}</PortalCard>
            <PortalCard title="Insurance">{c.revenue_streams.insurance}</PortalCard>
            <PortalCard title="Promotions">{c.revenue_streams.promotions}</PortalCard>
            <PortalCard title="Airline Partnerships">{c.revenue_streams.airline_partnerships}</PortalCard>
          </div>
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ color: '#7851A9', fontSize: '15px', marginBottom: '12px' }}>Per-Transaction Pricing (Both Sides Pay)</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#7851A9', color: '#fff' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Tier</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Developed (30%)</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Mid-Economy (40%)</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Developing (30%)</th>
                  </tr>
                </thead>
                <tbody>
                  {['door_to_door', 'city_to_city', 'state_to_state', 'country_to_country'].map((k) => (
                    <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 600, textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.revenue_streams.per_transaction.developed_30pct[k]}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.revenue_streams.per_transaction.mid_economy_40pct[k]}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.revenue_streams.per_transaction.developing_30pct[k]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Financial Projections */}
          <SectionTitle>Financial Detail — P&L</SectionTitle>
          <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>Metric</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Year 1</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Year 2</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Year 3</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Active Users', 'active_users'],
                  ['FA %', 'fa_percentage'],
                  ['Annual Deliveries', 'deliveries'],
                  ['Subscription Revenue', 'subscription_revenue'],
                  ['Base Charge Revenue', 'base_charge_revenue'],
                  ['Insurance & Promos', 'insurance_promotions'],
                  ['Total Revenue', 'total_revenue'],
                  ['Operating Expenses', 'operating_expenses'],
                  ['Net Income', 'net_income'],
                ].map(([label, key], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', fontWeight: label.includes('Revenue') || label.includes('Income') ? 700 : 400, background: label === 'Net Income' ? 'rgba(120,81,169,0.05)' : 'transparent' }}>
                    <td style={{ padding: '8px 14px' }}>{label}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.financial_projections.year_1[key]}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.financial_projections.year_2[key]}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>{c.financial_projections.year_3[key]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ textAlign: 'center', marginTop: '12px', fontWeight: 700, color: '#059669', fontSize: '15px' }}>Cumulative Net Income (Year 3): {c.financial_projections.cumulative_net_income_year3}</p>
            {c.financial_projections.note && (
              <p style={{ marginTop: '10px', fontSize: '11.5px', color: '#9CA3AF', lineHeight: 1.5, fontStyle: 'italic' }}>{c.financial_projections.note}</p>
            )}
          </div>

          {/* ===== HOW IT WORKS ===== */}
          <SectionTitle>How the App Works</SectionTitle>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '-6px 0 18px', maxWidth: '680px', lineHeight: 1.6 }}>A complete two-sided journey — from a US pickup to a Ghana delivery — showing the four service tiers, transparent pricing, flexible payments, live tracking and the courier's verified pickup & inspection flow. Every screen is from the live app.</p>

          <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#7851A9', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px' }}>The Sender Journey</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '16px', marginBottom: '28px' }}>
            <AppShot src="/investor/app-home.webp" title="1. One Home, Two Roles" desc="Senders & couriers start on a single branded screen — pick a country to begin." />
            <AppShot src="/investor/app-country.webp" title="2. Truly Global" desc="Ship to/from any country with full currency & locale support." />
            <AppShot src="/investor/app-send.webp" title="3. Pickup & Drop-off" desc="Smart address search validates both ends of the journey." />
            <AppShot src="/investor/app-tiers.webp" title="4. Four Service Tiers" desc="Door-2-Door, City-2-City, State-2-State & Country-2-Country." />
            <AppShot src="/investor/app-route.webp" title="5. Cross-Border Route" desc="Example: New York → Kumasi, Ghana on the international tier." />
            <AppShot src="/investor/app-price.webp" title="6. Transparent Pricing" desc="Live quote: base fare, distance, platform fee & courier earnings." />
            <AppShot src="/investor/app-pay.webp" title="7. Flexible Payments" desc="Card, Apple Pay, Google Pay or Cash on Delivery." />
            <AppShot src="/investor/app-placed.webp" title="8. Order Placed" desc="Instantly broadcast to nearby couriers for acceptance." />
          </div>

          <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#059669', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px' }}>Live Tracking, the Courier Experience & Trust</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '16px', marginBottom: '32px' }}>
            <AppShot src="/investor/app-track.webp" title="9. Real-Time Tracking" desc="Senders watch the courier and live ETA on the map from pickup to drop-off." />
            <AppShot src="/investor/app-courier-home.webp" title="10. Courier Dashboard" desc="Earnings, active gigs and identity verification in one place." />
            <AppShot src="/investor/app-courier-gigs.webp" title="11. Gigs on Your Route" desc="Couriers see paid gigs that match routes they already travel." />
            <AppShot src="/investor/app-courier-accept.webp" title="12. Accept a Gig" desc="One tap to accept and start earning on the trip." />
            <AppShot src="/investor/app-courier-pickup.webp" title="13. Guided Navigation" desc="Turn-by-turn to pickup, with optional flight details for tracking." />
            <AppShot src="/investor/app-courier-inspect.webp" title="14. Safety Inspection" desc="Photo/video package inspection with a report-suspicious-items gate." />
            <AppShot src="/investor/app-courier-nav.webp" title="15. On the Way" desc="Live courier location streams back to the sender until delivered." />
          </div>

          {/* Cost Structure */}
          <SectionTitle>Cost Structure</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <PortalCard title="Fixed Annual Costs">
              {Object.entries(c.cost_structure.fixed_annual).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </PortalCard>
            <PortalCard title="Variable Annual Costs">
              {Object.entries(c.cost_structure.variable_annual).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 600, fontSize: '12px' }}>{v}</span>
                </div>
              ))}
            </PortalCard>
            <PortalCard title="Already Incurred (One-Time)">
              {Object.entries(c.cost_structure.already_incurred).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </PortalCard>
          </div>

          {/* Key Partners & Activities */}
          <SectionTitle>Key Partners & Activities</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <PortalCard title="Strategic Partners"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.key_partners.strategic.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}</ul></PortalCard>
            <PortalCard title="Service Partners"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.key_partners.service.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}</ul></PortalCard>
            <PortalCard title="Distribution Partners"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.key_partners.distribution.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}</ul></PortalCard>
          </div>

          {/* Channels */}
          <SectionTitle>Channels</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <PortalCard title="Digital Channels"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.channels.digital.map((ch, i) => <li key={i} style={{ marginBottom: '4px' }}>{ch}</li>)}</ul></PortalCard>
            <PortalCard title="Physical Channels"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.channels.physical.map((ch, i) => <li key={i} style={{ marginBottom: '4px' }}>{ch}</li>)}</ul></PortalCard>
            <PortalCard title="Referral Channels"><ul style={{ paddingLeft: '16px', margin: 0 }}>{c.channels.referral.map((ch, i) => <li key={i} style={{ marginBottom: '4px' }}>{ch}</li>)}</ul></PortalCard>
          </div>

          {/* Competitive Advantages */}
          <SectionTitle>Competitive Advantages</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: '32px' }}>
            {c.competitive_advantages.map((adv, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: '10px', padding: '14px 18px', borderLeft: '3px solid #7851A9', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 700, color: '#1F2937', marginBottom: '4px' }}>{adv.title}</h4>
                <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: 1.5, margin: 0 }}>{adv.desc}</p>
              </div>
            ))}
          </div>

          {/* Customer Segments */}
          <SectionTitle>Customer Segments</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <PortalCard title="Primary Couriers (Flight Attendants)">{c.customer_segments.primary_couriers}</PortalCard>
            <PortalCard title="Secondary Couriers">{c.customer_segments.secondary_couriers}</PortalCard>
            <PortalCard title="Senders">{c.customer_segments.senders}</PortalCard>
          </div>

          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: '12px' }}>
            <p>This document is confidential and proprietary to Nighteengale Group Ltd.</p>
            <p>Unauthorized reproduction, distribution, or disclosure is strictly prohibited under the executed NDA.</p>
            <p style={{ marginTop: '8px' }}>Viewing as: <strong>{investorEmail}</strong></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ maxWidth: '480px', width: '100%' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <img src="/logo.png" alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
            <span style={{ color: '#fff', fontSize: '24px', fontWeight: 800 }}>Spee'V'Dee</span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', letterSpacing: '2px' }}>NIGHTEENGALE GROUP LTD.</p>
        </div>

        {/* Progress Steps */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{ width: '80px', height: '4px', borderRadius: '2px', background: step >= s ? '#7851A9' : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
          ))}
        </div>

        {/* Step 1: Register */}
        {step === 1 && (
          <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '32px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Lock size={28} color="#7851A9" />
              <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>Investor Portal Access</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Submit your details to begin the verification process</p>
            </div>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#7851A9' }} />
                  <input data-testid="investor-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="investor@example.com" style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#7851A9' }} />
                  <input data-testid="investor-phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
              <button data-testid="register-btn" type="submit" disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {loading ? 'Submitting...' : <><span>Continue</span><ArrowRight size={16} /></>}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: NDA */}
        {step === 2 && (
          <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '32px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Shield size={28} color="#7851A9" />
              <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>Non-Disclosure Agreement</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Please read and scroll to the bottom to accept</p>
            </div>
            <div data-testid="nda-content" onScroll={handleNdaScroll} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '20px', maxHeight: '320px', overflowY: 'auto', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <pre style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11.5px', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{ndaText}</pre>
            </div>
            {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
            <button data-testid="accept-nda-btn" onClick={handleAcceptNDA} disabled={!ndaScrolled || loading} style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: ndaScrolled ? 'linear-gradient(135deg, #7851A9, #5B2D8E)' : 'rgba(255,255,255,0.1)', color: ndaScrolled ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: '15px', fontWeight: 700, cursor: !ndaScrolled || loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {loading ? 'Sending Code...' : !ndaScrolled ? 'Scroll to read full NDA' : <><CheckCircle size={16} /><span>I Accept — Send Verification Code</span></>}
            </button>
          </div>
        )}

        {/* Step 3: Verify Code */}
        {step === 3 && (
          <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '32px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Mail size={28} color="#7851A9" />
              <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>Verify Your Email</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>A 6-digit code has been sent to <strong style={{ color: '#7851A9' }}>{email}</strong></p>
            </div>
            <form onSubmit={handleVerify}>
              <div style={{ marginBottom: '20px' }}>
                <input data-testid="verify-code" type="text" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} style={{ width: '100%', padding: '16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '28px', fontWeight: 700, textAlign: 'center', letterSpacing: '12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
              <button data-testid="verify-btn" type="submit" disabled={loading || code.length !== 6} style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: code.length === 6 ? 'linear-gradient(135deg, #7851A9, #5B2D8E)' : 'rgba(255,255,255,0.1)', color: code.length === 6 ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: '15px', fontWeight: 700, cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {loading ? 'Verifying...' : <><Lock size={16} /><span>Access Investor Portal</span></>}
              </button>
            </form>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', textAlign: 'center', marginTop: '16px' }}>Code expires in 30 minutes. Check your spam folder if not received.</p>
          </div>
        )}

        <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', textAlign: 'center', marginTop: '20px' }}>
          Nighteengale Group Ltd. &copy; {new Date().getFullYear()} — All rights reserved
        </p>
      </div>
    </div>
  );
};

// Helper components
const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1F2937', marginBottom: '16px', borderBottom: '2px solid #7851A9', paddingBottom: '8px', display: 'inline-block' }}>{children}</h3>
);

const PortalCard = ({ title, accent, children }) => (
  <div style={{ background: accent ? 'linear-gradient(135deg, #7851A9, #5B2D8E)' : '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: accent ? 'none' : '1px solid #e5e7eb' }}>
    <h4 style={{ fontSize: '14px', fontWeight: 700, color: accent ? '#fff' : '#7851A9', marginBottom: '10px', borderBottom: `2px solid ${accent ? 'rgba(255,255,255,0.2)' : 'rgba(120,81,169,0.15)'}`, paddingBottom: '6px' }}>{title}</h4>
    <div style={{ fontSize: '12.5px', color: accent ? 'rgba(255,255,255,0.9)' : '#374151', lineHeight: 1.6 }}>{children}</div>
  </div>
);

const StatCard = ({ label, value, accent }) => (
  <div style={{ background: accent ? 'linear-gradient(135deg,#7851A9,#5B2D8E)' : '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: accent ? 'none' : '1px solid #eee' }}>
    <div style={{ fontSize: '22px', fontWeight: 900, color: accent ? '#fff' : '#7851A9' }}>{value}</div>
    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: accent ? 'rgba(255,255,255,0.8)' : '#9CA3AF', marginTop: '4px' }}>{label}</div>
  </div>
);

const AppShot = ({ src, title, desc }) => (
  <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
    <div style={{ background: '#0f172a', padding: '14px', display: 'flex', justifyContent: 'center' }}>
      <img src={src} alt={title} style={{ width: '150px', borderRadius: '10px', boxShadow: '0 6px 20px rgba(0,0,0,0.3)' }} />
    </div>
    <div style={{ padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: '14px', color: '#1F2937' }}>{title}</h4>
      <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', lineHeight: 1.5 }}>{desc}</p>
    </div>
  </div>
);

export default InvestorPortal;
